import React, { useEffect, useState } from "react";
import {
  Modal,
  TextInput,
  Button,
  Group,
  Stack,
  Alert,
  Select,
  NumberInput,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";

const EditOfficeEntityModal = ({
  opened,
  onClose,
  onSave,
  entity,
  entityLabel = "Office",
}) => {
  const [name, setName] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [officeId, setOfficeId] = useState("");
  const [authKey, setAuthKey] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [authMethod, setAuthMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (opened) {
      const connection = entity?.fieldroutes_connection;
      setName(entity?.name || "");
      setSubdomain(connection?.subdomain || "");
      setOfficeId(connection?.fr_office_id ?? "");
      setAuthKey(connection?.auth_key || "");
      setAuthToken(connection?.auth_token || "");
      setAuthMethod(connection?.auth_method || null);
      setError("");
    }
  }, [opened, entity]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    if (authMethod === "api") {
      if (!subdomain.trim()) {
        setError("Subdomain is required for API auth");
        return;
      }
      if (officeId === null || officeId === "") {
        setError("Office ID is required for API auth");
        return;
      }
      if (!authKey.trim()) {
        setError("Auth key is required for API auth");
        return;
      }
      if (!authToken.trim()) {
        setError("Auth token is required for API auth");
        return;
      }
    }

    setLoading(true);
    setError("");

    try {
      await onSave({
        name: name.trim(),
        subdomain: subdomain.trim() || null,
        fr_office_id: officeId ? Number(officeId) : null,
        auth_key: authKey.trim() || null,
        auth_token: authToken.trim() || null,
        auth_method: authMethod,
      });
      onClose();
    } catch (err) {
      setError(err.message || `Failed to update ${entityLabel.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError("");
    onClose();
  };

  const canSubmit =
    !!name.trim() &&
    (authMethod !== "api" ||
      (!!subdomain.trim() &&
        !!officeId &&
        !!authKey.trim() &&
        !!authToken.trim()));

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={`Edit ${entityLabel}`}
      size="md"
    >
      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        <TextInput
          label="Name"
          placeholder={`Enter ${entityLabel.toLowerCase()} name`}
          value={name}
          onChange={e => setName(e.target.value)}
          required
          data-autofocus
        />

        <TextInput
          label={`Subdomain${authMethod === "api" ? " *" : ""}`}
          placeholder="Enter subdomain"
          value={subdomain}
          onChange={e => setSubdomain(e.target.value)}
        />

        <NumberInput
          label={`Office ID${authMethod === "api" ? " *" : ""}`}
          placeholder="Enter office ID"
          value={officeId}
          onChange={setOfficeId}
        />

        <TextInput
          label={`Auth Key${authMethod === "api" ? " *" : ""}`}
          placeholder="Enter auth key"
          value={authKey}
          onChange={e => setAuthKey(e.target.value)}
        />

        <TextInput
          label={`Auth Token${authMethod === "api" ? " *" : ""}`}
          placeholder="Enter auth token"
          value={authToken}
          onChange={e => setAuthToken(e.target.value)}
        />

        <Select
          label="Auth Method"
          placeholder="Select auth method"
          data={[
            { value: "api", label: "API" },
            { value: "csv", label: "CSV" },
          ]}
          value={authMethod}
          onChange={setAuthMethod}
          clearable
        />

        <Group justify="flex-end" gap="sm">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={loading} disabled={!canSubmit}>
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default EditOfficeEntityModal;
